import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../config/api';

/**
 * FirmPipelinesWidget — compact version of PipelinesTab. Lists this firm's
 * recruitment pipelines with an inline create affordance; opens the board on
 * click. Data contract: GET /pipelines?firm_id=, POST /pipelines.
 */
export default function FirmPipelinesWidget({ firmId }) {
    const [pipelines, setPipelines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState('');
    const navigate = useNavigate();

    const token = localStorage.getItem('authToken');

    useEffect(() => { fetchPipelines(); /* eslint-disable-next-line */ }, [firmId]);

    const fetchPipelines = async () => {
        try {
            const res = await fetch(`${API_URL}/pipelines?firm_id=${firmId}`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            setPipelines(data.filter(p => p.firm_id === parseInt(firmId)));
        } catch (e) { console.error('Error fetching pipelines:', e); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        if (!name.trim()) return;
        try {
            const res = await fetch(`${API_URL}/pipelines`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ firm_id: parseInt(firmId), name, created_by: 1 }),
            });
            if (!res.ok) throw new Error('Failed to create pipeline');
            setName(''); setCreating(false); fetchPipelines();
        } catch (e) { alert(e.message); }
    };

    if (loading) return <p className="text-sm text-gray-400">Loading pipelines…</p>;

    return (
        <div className="space-y-3">
            {creating ? (
                <div className="space-y-2">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setName(''); } }}
                        autoFocus
                        placeholder="e.g., Senior PM - Chicago"
                        className="w-full px-2 py-1.5 text-sm bg-gray-50 border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <div className="flex justify-end gap-2">
                        <button onClick={() => { setCreating(false); setName(''); }} className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                        <button onClick={handleCreate} className="px-2.5 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700">Create</button>
                    </div>
                </div>
            ) : (
                <button onClick={() => setCreating(true)} className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-emerald-700 border border-dashed border-emerald-300 rounded hover:bg-emerald-50 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    New pipeline
                </button>
            )}

            {pipelines.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-3">No pipelines yet</p>
            ) : (
                <div className="space-y-1.5">
                    {pipelines.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => navigate(`/pipeline/${p.id}`)}
                            className="w-full flex items-center justify-between px-2.5 py-2 bg-gray-50 rounded hover:bg-emerald-50 transition-colors text-left group"
                        >
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                                <div className="text-[10px] text-gray-400">Created {new Date(p.created_at).toLocaleDateString()}</div>
                            </div>
                            <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
